import React, { useState, useEffect, useCallback } from 'react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import { priceAnalyticsService } from '../../application/services/PriceAnalyticsService';
import { syncService } from '../../application/services/SyncService';
import type { MarkupSettings } from '../../infrastructure/repositories/SyncRepository';
import type { PriceAnalyticsResponse, PriceSizeAnalytics, PriceProductItem, PriceCollectStatus } from '../../infrastructure/repositories/PriceAnalyticsRepository';

const PC: Record<string, string> = {
  goatPrice: '#10B981', poizonPrice: '#F59E0B', stockxPrice: '#3B82F6',
  sortagePrice: '#8B5CF6', wayoffPrice: '#EC4899', warehousePrice: '#22C55E',
};
const PL: Record<string, string> = {
  goatPrice: 'Goat', poizonPrice: 'Poizon', stockxPrice: 'StockX',
  sortagePrice: 'Sortage', wayoffPrice: 'Wayoff', warehousePrice: 'Наша цена',
};
const PLATFORM_KEYS: string[] = ['goatPrice', 'poizonPrice', 'stockxPrice', 'sortagePrice', 'wayoffPrice', 'warehousePrice'];
const CHART_PLATFORM_KEYS: string[] = ['goatPrice', 'poizonPrice', 'stockxPrice', 'sortagePrice', 'wayoffPrice'];
const SIZE_COLORS = ['#EF4444','#F59E0B','#10B981','#3B82F6','#8B5CF6','#EC4899','#F97316','#06B6D4','#84CC16','#A855F7','#E11D48','#0EA5E9','#D946EF','#14B8A6','#FB923C','#6366F1','#22D3EE','#F43F5E','#A3E635','#C084FC'];

const fmt = (v: number) => new Intl.NumberFormat('ru-RU').format(Math.round(v));
const PAGE_SIZE = 20;

const calcAvg = (prices: (number|undefined|null)[]): number | null => {
  const valid = prices.filter((p): p is number => p != null && p > 0);
  return valid.length > 0 ? valid.reduce((a, b) => a + b, 0) / valid.length : null;
};

// Сортировка размеров числово
const sortSizes = (sizes: PriceSizeAnalytics[]) => {
  return [...sizes].sort((a, b) => {
    const na = parseFloat(a.usSize.replace('W', '')) || 0;
    const nb = parseFloat(b.usSize.replace('W', '')) || 0;
    return na - nb;
  });
};

// Стиль кнопки
const btnCls = (active: boolean, color?: string) => {
  if (active && color) return `px-3 py-1.5 text-sm font-medium rounded-md border text-white` ;
  if (active) return `px-3 py-1.5 text-sm font-medium rounded-md border bg-indigo-600 text-white border-indigo-600`;
  return `px-3 py-1.5 text-sm font-medium rounded-md border bg-white dark:bg-gray-700 text-gray-700 dark:text-gray-300 border-gray-300 dark:border-gray-600 hover:bg-gray-100 dark:hover:bg-gray-600`;
};

type ChartMode = 'bySize' | 'byPlatform';

export const PriceAnalyticsPage: React.FC = () => {
  const [products, setProducts] = useState<PriceProductItem[]>([]);
  const [totalProducts, setTotalProducts] = useState(0);
  const [page, setPage] = useState(0);
  const [search, setSearch] = useState('');
  const [expandedSku, setExpandedSku] = useState<string | null>(null);
  const [detail, setDetail] = useState<PriceAnalyticsResponse | null>(null);
  const [selectedSize, setSelectedSize] = useState<string | 'all' | null>(null);
  const [selectedPlatform, setSelectedPlatform] = useState<string | 'all' | null>(null);
  const [chartMode, setChartMode] = useState<ChartMode>('bySize');
  const [status, setStatus] = useState<PriceCollectStatus | null>(null);
  const [loading, setLoading] = useState(false);
  const [settings, setSettings] = useState<MarkupSettings | null>(null);
  const [error, setError] = useState<string | null>(null);

  const loadProducts = useCallback(async (p = page, q = search) => {
    try {
      const [resp, st] = await Promise.all([priceAnalyticsService.getProducts(PAGE_SIZE, p * PAGE_SIZE, q), priceAnalyticsService.getStatus()]);
      if (!settings) { try { setSettings(await syncService.getSettings()); } catch {} }
      setProducts(resp.products || []); setTotalProducts(resp.total); setStatus(st);
    } catch {}
  }, [page, search]);
  useEffect(() => { loadProducts(); }, [loadProducts]);
  useEffect(() => {
    if (!status?.isRunning) return;
    const iv = setInterval(async () => { try { const st = await priceAnalyticsService.getStatus(); setStatus(st); if (!st.isRunning) loadProducts(); } catch {} }, 2000);
    return () => clearInterval(iv);
  }, [status?.isRunning, loadProducts]);

  // Debounce поиска
  const [searchInput, setSearchInput] = useState('');
  useEffect(() => {
    const timer = setTimeout(() => { setSearch(searchInput); setPage(0); }, 400);
    return () => clearTimeout(timer);
  }, [searchInput]);

  const toggleExpand = async (sku: string) => {
    if (expandedSku === sku) { setExpandedSku(null); setDetail(null); return; }
    try {
      setLoading(true); setError(null);
      const d = await priceAnalyticsService.getAnalytics(sku);
      d.sizes = d.sizes || [];
      setDetail(d); setExpandedSku(sku);
      setSelectedSize(d.sizes.length > 0 ? d.sizes[0]!.usSize : null);
      setSelectedPlatform(CHART_PLATFORM_KEYS[0]!); setChartMode('bySize');
    } catch (e: any) { setError(e?.message || 'Ошибка'); } finally { setLoading(false); }
  };

  const handleCollect = async () => { try { await priceAnalyticsService.triggerCollect(); setStatus(await priceAnalyticsService.getStatus()); } catch {} };
  const handleCancel = async () => { try { await priceAnalyticsService.cancelCollect(); } catch {} };
  const totalPages = Math.ceil(totalProducts / PAGE_SIZE);

  // Chart: один размер → все площадки (включая склад)
  const buildBySizeChart = (sd: PriceSizeAnalytics) => {
    const active = PLATFORM_KEYS.filter(k => sd.history.some(h => (h as any)[k] > 0));
    return { data: sd.history, lines: active.map(k => ({ key: k, color: PC[k] || '#888', label: PL[k] || k })) };
  };

  // Chart: одна площадка → все размеры
  const buildByPlatformChart = (platform: string) => {
    if (!detail) return { data: [] as any[], lines: [] as any[] };
    const allDates = new Set<string>();
    detail.sizes.forEach(s => s.history.forEach(h => allDates.add(h.date)));
    const dates = Array.from(allDates).sort();
    const data = dates.map(date => {
      const point: any = { date };
      detail.sizes.forEach(s => { const h = s.history.find(h => h.date === date); if (h) point[`s_${s.usSize}`] = (h as any)[platform] || null; });
      return point;
    });
    const lines = detail.sizes.filter(s => s.history.some(h => (h as any)[platform] > 0)).map((s, i) => ({ key: `s_${s.usSize}`, color: SIZE_COLORS[i % SIZE_COLORS.length]!, label: `US ${s.usSize}` }));
    return { data, lines };
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div><h1 className="text-2xl font-bold text-gray-900 dark:text-white">Аналитика цен</h1><p className="text-sm text-gray-500 dark:text-gray-400 mt-1">Отслеживание цен по площадкам за 30 дней</p></div>
        {status?.isRunning ? (
          <div className="flex items-center gap-3"><span className="text-xs text-gray-400">{status.progress ? `${status.progress.processed}/${status.progress.totalProducts}` : ''}</span><button onClick={handleCancel} className="px-4 py-2 text-sm font-medium rounded-md text-white bg-red-600 hover:bg-red-700">Отменить</button></div>
        ) : (<button onClick={handleCollect} className="px-4 py-2 border border-gray-300 dark:border-gray-600 text-sm font-medium rounded-md text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700/50">Собрать цены</button>)}
      </div>

      {/* Progress */}
      {status?.isRunning && status.progress && status.progress.totalProducts > 0 && (
        <div className="bg-white dark:bg-gray-800 shadow rounded-lg p-4">
          <div className="flex justify-between mb-2"><div className="flex items-center gap-2"><div className="animate-spin rounded-full h-4 w-4 border-b-2 border-indigo-600"></div><span className="text-sm font-medium text-gray-900 dark:text-white">Сбор цен</span></div><span className="text-sm text-gray-400">{status.progress.processed}/{status.progress.totalProducts}</span></div>
          <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2 mb-2"><div className="bg-indigo-600 h-2 rounded-full transition-all" style={{width:`${Math.round((status.progress.processed/status.progress.totalProducts)*100)}%`}}></div></div>
          <div className="flex justify-between text-xs text-gray-400"><span className="truncate max-w-xs">{status.progress.currentSku}</span><span>Записей: {status.progress.recordsWritten}</span></div>
        </div>
      )}
      {error && <div className="bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-800 text-red-700 dark:text-red-300 px-4 py-3 rounded-lg">{error}</div>}

      {/* TABLE */}
      <div className="bg-white dark:bg-gray-800 shadow-sm border border-gray-200 dark:border-gray-700 rounded-lg">
        <div className="px-4 py-3 border-b border-gray-200 dark:border-gray-700">
          <input type="text" value={searchInput} onChange={e => setSearchInput(e.target.value)} placeholder="Поиск по SKU, названию, бренду..." className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-indigo-500 focus:border-indigo-500" />
        </div>
        <div className="overflow-x-auto">
          <table className="min-w-full border-collapse">
            <thead className="bg-gray-50 dark:bg-gray-700 sticky top-0 z-10">
              <tr>
                <th className="py-2.5 px-4 text-left text-xs font-semibold text-gray-900 dark:text-white uppercase w-48 border-b border-r border-gray-300 dark:border-gray-600">SKU</th>
                <th className="py-2.5 px-3 text-left text-xs font-semibold text-gray-900 dark:text-white uppercase border-b border-r border-gray-300 dark:border-gray-600">Название</th>
                <th className="py-2.5 px-3 text-left text-xs font-semibold text-gray-900 dark:text-white uppercase w-24 border-b border-r border-gray-300 dark:border-gray-600">Бренд</th>
                <th className="py-2.5 px-3 text-center text-xs font-semibold text-gray-900 dark:text-white uppercase w-10 border-b border-gray-300 dark:border-gray-600"></th>
              </tr>
            </thead>
            <tbody>
              {products.length === 0 && <tr><td colSpan={4} className="px-4 py-8 text-center text-gray-500 dark:text-gray-400">{search ? 'Ничего не найдено' : 'Нет данных. Запустите сбор цен.'}</td></tr>}
              {products.map(p => (
                <React.Fragment key={p.sku}>
                  <tr className="border-b border-gray-100 dark:border-gray-700/50 hover:bg-gray-50 dark:hover:bg-gray-700/30 cursor-pointer" onClick={() => toggleExpand(p.sku)}>
                    <td className="py-2.5 px-4 text-sm border-r border-gray-200 dark:border-gray-700">
                      <div className="flex items-center gap-3">
                        {p.imageUrl ? <img src={p.imageUrl} alt="" className="h-12 w-12 object-cover rounded border border-gray-200 dark:border-gray-600 flex-shrink-0"/> : <div className="h-12 w-12 bg-gray-100 dark:bg-gray-700 rounded border border-gray-200 dark:border-gray-600 flex-shrink-0"/>}
                        <span className="text-indigo-600 dark:text-indigo-400 font-medium">{p.sku}</span>
                      </div>
                    </td>
                    <td className="py-2.5 px-3 text-sm text-gray-500 dark:text-gray-300 border-r border-gray-200 dark:border-gray-700">{p.name}</td>
                    <td className="py-2.5 px-3 text-sm text-gray-500 dark:text-gray-300 border-r border-gray-200 dark:border-gray-700">{p.brand}</td>
                    <td className="py-2.5 px-3 text-center"><svg className={`w-4 h-4 mx-auto text-gray-400 transition-transform ${expandedSku === p.sku ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7"/></svg></td>
                  </tr>

                  {/* Collapsed: price cards */}
                  {p.sampleSize && expandedSku !== p.sku && (
                    <tr className="border-b border-gray-200 dark:border-gray-700">
                      <td className="py-2 px-4 border-r border-gray-200 dark:border-gray-700">
                        <span className="text-xs font-medium text-gray-400">US {p.sampleSize} <span className="text-gray-500">({p.sizesCount})</span></span>
                      </td>
                      <td colSpan={3} className="py-2 px-3">
                        <div className="flex items-center gap-3 flex-wrap">
                          {PLATFORM_KEYS.map(k => {
                            const price = (p as any)[k] as number | undefined;
                            return (
                              <div key={k} className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md border border-gray-200 dark:border-gray-600 bg-gray-50 dark:bg-gray-700/50">
                                <span className="w-2 h-2 rounded-full flex-shrink-0" style={{backgroundColor: PC[k] || '#888'}}></span>
                                <span className="text-xs text-gray-400">{PL[k] || k}:</span>
                                <span className="text-sm font-semibold" style={{color: price && price > 0 ? (PC[k] || '#888') : '#6B7280'}}>
                                  {price && price > 0 ? `${fmt(price)} P` : '0 P'}
                                </span>
                              </div>
                            );
                          })}
                        </div>
                      </td>
                    </tr>
                  )}

                  {/* EXPANDED */}
                  {expandedSku === p.sku && detail && (
                    <tr className="border-b border-gray-200 dark:border-gray-700">
                      <td colSpan={4} className="p-0">
                        <div className="bg-gray-50 dark:bg-gray-900/50 p-5 space-y-4">
                          {/* Mode toggle */}
                          <div className="flex items-center gap-2">
                            <button onClick={() => setChartMode('bySize')} className={btnCls(chartMode === 'bySize')}>По размеру</button>
                            <button onClick={() => setChartMode('byPlatform')} className={btnCls(chartMode === 'byPlatform')}>По площадке</button>
                          </div>

                          {/* === BY SIZE === */}
                          {chartMode === 'bySize' && (
                            <>
                              <div className="flex flex-wrap gap-2">
                                {sortSizes(detail.sizes).map(s => (
                                  <button key={s.usSize} onClick={() => setSelectedSize(s.usSize)} className={btnCls(selectedSize === s.usSize)}>
                                    {s.usSize} US
                                  </button>
                                ))}
                                <button onClick={() => setSelectedSize('all')} className={btnCls(selectedSize === 'all')}>Все</button>
                              </div>

                              {/* Single size */}
                              {selectedSize && selectedSize !== 'all' && (() => {
                                const sd = detail.sizes.find(s => s.usSize === selectedSize);
                                if (!sd) return null;
                                const cur = sd.current || {} as any;
                                const sp = sd.shopPrices;
                                const fAvg = calcAvg([cur.goatPrice, cur.poizonPrice, cur.stockxPrice]);
                                const rAvg = calcAvg([cur.sortagePrice]);
                                const diff = fAvg && rAvg ? rAvg - fAvg : null;
                                const diffPct = fAvg && diff ? (diff/fAvg)*100 : null;
                                const chart = buildBySizeChart(sd);

                                // Рассчитать наценку % относительно raw цены
                                const markupPct = (ourPrice?: number, rawPrice?: number): string => {
                                  if (!ourPrice || !rawPrice || rawPrice <= 0) return '';
                                  const pct = ((ourPrice - rawPrice) / rawPrice) * 100;
                                  return ` (${pct>0?'+':''}${pct.toFixed(0)}%)`;
                                };

                                // Raw цена из источника abroad (или мин. из всех зарубежных)
                                const abroadSourceKey: Record<string, string> = { stockx: 'stockxPrice', poizon: 'poizonPrice', goat: 'goatPrice' };
                                const abroadRawPrice = sp?.abroadSource ? cur[abroadSourceKey[sp.abroadSource]] : undefined;

                                return (
                                  <>
                                    <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3">
                                      {PLATFORM_KEYS.filter(k => k !== 'warehousePrice').map(k => <PriceCard key={k} label={PL[k] || k} price={(cur)[k]} color={PC[k] || '#888'} />)}
                                    </div>

                                    {/* Наши цены магазина с наценкой */}
                                    {sp && (sp.inStock || sp.warehouse || sp.abroad) && (
                                      <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-4 mt-3">
                                        <h4 className="text-sm font-semibold text-gray-900 dark:text-white mb-3">Наши цены в магазине</h4>
                                        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                                          {sp.inStock && (
                                            <div className="bg-green-50 dark:bg-green-900/20 rounded-lg border border-green-200 dark:border-green-800 p-3">
                                              <div className="text-xs text-gray-500 dark:text-gray-400">В наличии</div>
                                              <div className="text-xl font-bold text-green-600 dark:text-green-400">{fmt(sp.inStock)} ₽</div>
                                              {fAvg && <div className="text-xs text-gray-500 dark:text-gray-400 mt-1">vs ср. заруб. {fmt(fAvg)} ₽<span className={((sp.inStock-fAvg)/fAvg*100)<0?'text-green-500':'text-red-500'}>{markupPct(sp.inStock, fAvg)}</span></div>}
                                            </div>
                                          )}
                                          {sp.warehouse && (
                                            <div className="bg-indigo-50 dark:bg-indigo-900/20 rounded-lg border border-indigo-200 dark:border-indigo-800 p-3">
                                              <div className="text-xs text-gray-500 dark:text-gray-400">Со склада</div>
                                              <div className="text-xl font-bold text-indigo-600 dark:text-indigo-400">{fmt(sp.warehouse)} ₽</div>
                                              {cur.warehousePrice && <div className="text-xs text-gray-500 dark:text-gray-400 mt-1">raw склад {fmt(cur.warehousePrice)} ₽<span className={((sp.warehouse-cur.warehousePrice)/cur.warehousePrice*100)<0?'text-green-500':'text-red-500'}>{markupPct(sp.warehouse, cur.warehousePrice)}</span></div>}
                                            </div>
                                          )}
                                          {sp.abroad && (
                                            <div className="bg-orange-50 dark:bg-orange-900/20 rounded-lg border border-orange-200 dark:border-orange-800 p-3">
                                              <div className="text-xs text-gray-500 dark:text-gray-400">Под заказ {sp.abroadSource && `(${sp.abroadSource})`}</div>
                                              <div className="text-xl font-bold text-orange-600 dark:text-orange-400">{fmt(sp.abroad)} ₽</div>
                                              {abroadRawPrice && <div className="text-xs text-gray-500 dark:text-gray-400 mt-1">raw {sp.abroadSource} {fmt(abroadRawPrice)} ₽<span className={((sp.abroad-abroadRawPrice)/abroadRawPrice*100)<0?'text-green-500':'text-red-500'}>{markupPct(sp.abroad, abroadRawPrice)}</span></div>}
                                            </div>
                                          )}
                                        </div>
                                      </div>
                                    )}
                                    {(() => {
                                      const wh = cur.warehousePrice as number | undefined;
                                      const sortP = cur.sortagePrice as number | undefined;
                                      const wayP = cur.wayoffPrice as number | undefined;

                                      const diffCard = (label: string, base: number | undefined, market: number | undefined) => {
                                        if (!base || base <= 0 || !market || market <= 0) return null;
                                        const d = base - market;
                                        const pct = (d / market) * 100;
                                        return <SmallCard label={label} value={`${d>0?'+':''}${fmt(d)} P (${pct>0?'+':''}${pct.toFixed(0)}%)`} color={d < 0 ? 'text-green-500' : d === 0 ? 'text-gray-400' : 'text-red-500'} />;
                                      };

                                      return (
                                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3">
                                          {fAvg && <SmallCard label="Ср. зарубежные" value={`${fmt(fAvg)} P`} color="text-blue-500"/>}
                                          {diffCard('Наша vs Sortage', wh, sortP)}
                                          {diffCard('Наша vs Wayoff', wh, wayP)}
                                          {diffCard('Наша vs Заруб.', wh, fAvg ?? undefined)}
                                        </div>
                                      );
                                    })()}
                                    {chart.lines.length > 0 && <ChartBlock title={`US ${sd.usSize} — все площадки`} data={chart.data} lines={chart.lines} />}
                                  </>
                                );
                              })()}

                              {/* All sizes */}
                              {selectedSize === 'all' && sortSizes(detail.sizes).map(sd => {
                                if (sd.history.length === 0) return null;
                                const chart = buildBySizeChart(sd);
                                if (chart.lines.length === 0) return null;
                                return <ChartBlock key={sd.usSize} title={`US ${sd.usSize}`} data={chart.data} lines={chart.lines} />;
                              })}
                            </>
                          )}

                          {/* === BY PLATFORM === */}
                          {chartMode === 'byPlatform' && (
                            <>
                              <div className="flex flex-wrap gap-2">
                                {CHART_PLATFORM_KEYS.map(k => (
                                  <button key={k} onClick={() => setSelectedPlatform(k)} className={btnCls(selectedPlatform === k)}>{PL[k] || k}</button>
                                ))}
                                <button onClick={() => setSelectedPlatform('all')} className={btnCls(selectedPlatform === 'all')}>Все</button>
                              </div>

                              {/* Single platform */}
                              {selectedPlatform && selectedPlatform !== 'all' && (() => {
                                const chart = buildByPlatformChart(selectedPlatform);
                                if (chart.lines.length === 0) return <div className="text-center py-6 text-gray-400">Нет данных для {PL[selectedPlatform] || selectedPlatform}</div>;
                                return <ChartBlock title={`${PL[selectedPlatform] || selectedPlatform} — все размеры`} data={chart.data} lines={chart.lines} />;
                              })()}

                              {/* All platforms */}
                              {selectedPlatform === 'all' && CHART_PLATFORM_KEYS.map(pk => {
                                const chart = buildByPlatformChart(pk);
                                if (chart.lines.length === 0) return null;
                                return <ChartBlock key={pk} title={`${PL[pk] || pk} — все размеры`} data={chart.data} lines={chart.lines} />;
                              })}
                            </>
                          )}

                          {/* Price table — always visible */}
                          <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 overflow-x-auto">
                            <table className="min-w-full border-collapse text-sm">
                              <thead className="bg-gray-50 dark:bg-gray-700/50">
                                <tr>
                                  <th className="py-2 px-3 text-left text-xs font-semibold text-gray-900 dark:text-white border-b border-r border-gray-300 dark:border-gray-600">Размер</th>
                                  {PLATFORM_KEYS.map(k => <th key={k} className="py-2 px-3 text-center text-xs font-semibold border-b border-r border-gray-300 dark:border-gray-600" style={{color:PC[k] || '#888'}}>{PL[k] || k}</th>)}
                                  <th className="py-2 px-3 text-center text-xs font-semibold text-gray-900 dark:text-white border-b border-r border-gray-300 dark:border-gray-600">vs Sortage</th>
                                  <th className="py-2 px-3 text-center text-xs font-semibold text-gray-900 dark:text-white border-b border-r border-gray-300 dark:border-gray-600">vs Wayoff</th>
                                  <th className="py-2 px-3 text-center text-xs font-semibold text-gray-900 dark:text-white border-b border-gray-300 dark:border-gray-600">vs Заруб.</th>
                                </tr>
                              </thead>
                              <tbody>
                                {sortSizes(detail.sizes).map(sd => {
                                  const cur = sd.current || {} as any;
                                  const fAvg = calcAvg([cur.goatPrice, cur.poizonPrice, cur.stockxPrice]);
                                  const rAvg = calcAvg([cur.sortagePrice]);
                                  const diff = fAvg && rAvg ? rAvg - fAvg : null;
                                  const diffPct = fAvg && diff ? (diff/fAvg)*100 : null;

                                  // Определяем источник цены "под заказ" по приоритету
                                  const prioMap: Record<string,string> = {stockx:'stockxPrice',poizon:'poizonPrice',goat:'goatPrice',sortage:'sortagePrice',wayoff:'wayoffPrice'};
                                  const priority = (settings?.abroadPricePriority || 'stockx,poizon,goat,sortage,wayoff').split(',').map((s: string) => s.trim());
                                  let sourceKey = '';
                                  for (const p of priority) { const pk = prioMap[p]; if (pk && cur[pk] && cur[pk] > 0) { sourceKey = pk; break; } }

                                  return (
                                    <tr key={sd.usSize} className="border-b border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-700/30">
                                      <td className="py-2 px-3 font-medium text-gray-900 dark:text-white border-r border-gray-200 dark:border-gray-700">US {sd.usSize}</td>
                                      {PLATFORM_KEYS.map(k => {
                                        const v = (cur)[k] as number | undefined;
                                        const isSource = k === sourceKey;
                                        return <td key={k} className={`py-2 px-3 text-center border-r border-gray-200 dark:border-gray-700 ${isSource ? 'ring-2 ring-indigo-500 ring-inset bg-indigo-50 dark:bg-indigo-900/20' : ''}`}><span style={{color: v && v > 0 ? (PC[k] || '#888') : '#6B7280'}} className="font-medium">{v && v > 0 ? `${fmt(v)}` : '0'}</span></td>;
                                      })}
                                      {(() => {
                                        const wh = (cur).warehousePrice as number | undefined;
                                        const sortP = (cur).sortagePrice as number | undefined;
                                        const wayP = (cur).wayoffPrice as number | undefined;
                                        const diffTd = (base: number | undefined, market: number | undefined, last?: boolean) => {
                                          if (!base || base <= 0 || !market || market <= 0) return <td className={`py-2 px-3 text-center ${last ? '' : 'border-r'} border-gray-200 dark:border-gray-700`}>--</td>;
                                          const d = base - market;
                                          const pct = (d / market) * 100;
                                          return <td className={`py-2 px-3 text-center ${last ? '' : 'border-r'} border-gray-200 dark:border-gray-700`}><span className={`font-medium ${d<0?'text-green-500':'text-red-500'}`}>{d>0?'+':''}{fmt(d)} ({pct>0?'+':''}{pct.toFixed(0)}%)</span></td>;
                                        };
                                        return <>{diffTd(wh, sortP)}{diffTd(wh, wayP)}{diffTd(wh, fAvg ?? undefined, true)}</>;
                                      })()}
                                    </tr>
                                  );
                                })}
                              </tbody>
                            </table>
                          </div>
                        </div>
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              ))}
            </tbody>
          </table>
        </div>
        {totalPages > 1 && (
          <div className="px-4 py-3 border-t border-gray-200 dark:border-gray-700 flex items-center justify-between">
            <span className="text-sm text-gray-500 dark:text-gray-400">Показано {page*PAGE_SIZE+1}–{Math.min((page+1)*PAGE_SIZE,totalProducts)} из {totalProducts}</span>
            <div className="flex items-center gap-2">
              <button onClick={() => setPage(p => Math.max(0, p-1))} disabled={page===0} className="px-3 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded-md text-gray-700 dark:text-gray-300 disabled:opacity-50">Предыдущая</button>
              <span className="text-sm text-gray-500 dark:text-gray-400">Страница {page+1} из {totalPages}</span>
              <button onClick={() => setPage(p => Math.min(totalPages-1, p+1))} disabled={page>=totalPages-1} className="px-3 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded-md text-gray-700 dark:text-gray-300 disabled:opacity-50">Следующая</button>
            </div>
          </div>
        )}
      </div>
      {loading && <div className="flex items-center justify-center h-16"><div className="animate-spin rounded-full h-6 w-6 border-b-2 border-indigo-600"></div></div>}
    </div>
  );
};

const PriceCard = ({ label, price, color }: { label: string; price?: number; color: string }) => (
  <div className="rounded-lg p-3 border border-gray-200 dark:border-gray-600 bg-gray-50 dark:bg-gray-700/50">
    <div className="text-xs text-gray-400">{label}</div>
    <div className="text-lg font-bold mt-0.5" style={{color: price && price > 0 ? color : '#6B7280'}}>{price && price > 0 ? `${fmt(price)} P` : '0 P'}</div>
  </div>
);

const SmallCard = ({ label, value, color }: { label: string; value: string; color: string }) => (
  <div className="bg-gray-50 dark:bg-gray-700/50 rounded-lg p-3 border border-gray-200 dark:border-gray-600">
    <div className="text-xs text-gray-400">{label}</div>
    <div className={`text-lg font-bold ${color}`}>{value}</div>
  </div>
);

const ChartBlock = ({ title, data, lines }: { title: string; data: any[]; lines: { key: string; color: string; label: string }[] }) => (
  <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-4">
    <h4 className="text-sm font-semibold text-gray-900 dark:text-white mb-3">{title}</h4>
    <ResponsiveContainer width="100%" height={280}>
      <LineChart data={data}>
        <CartesianGrid strokeDasharray="3 3" stroke="#374151" opacity={0.3} />
        <XAxis dataKey="date" tick={{ fontSize: 11, fill: '#9CA3AF' }} tickFormatter={v => v.slice(5)} />
        <YAxis tick={{ fontSize: 11, fill: '#9CA3AF' }} tickFormatter={v => new Intl.NumberFormat('ru-RU').format(Math.round(v as number))} width={70} />
        <Tooltip contentStyle={{ backgroundColor: '#1F2937', border: '1px solid #374151', borderRadius: '8px', color: '#F9FAFB' }}
          formatter={(v: number, n: string) => { const l = lines.find(x => x.key === n); return [`${new Intl.NumberFormat('ru-RU').format(Math.round(v))} P`, l?.label || n]; }} labelFormatter={l => l as string} />
        <Legend formatter={(v: string) => { const l = lines.find(x => x.key === v); return l?.label || v; }} wrapperStyle={{ fontSize: 12 }} />
        {lines.map(l => <Line key={l.key} type="monotone" dataKey={l.key} stroke={l.color} strokeWidth={2} dot={{ r: 3 }} connectNulls />)}
      </LineChart>
    </ResponsiveContainer>
  </div>
);
